/* Shared Book Lab export service + Export modal.
   Used by BOTH the AI runner and the Canvas Quick Actions.
   No format-specific logic duplicated — every UI calls exportBook(fmt).
*/
import { useState } from "react";
import {
  Download, FileText, FileType, BookOpen, Code2, Package, Loader2, X,
} from "lucide-react";
import { api } from "../../lib/apiClient";

/* ------------------------------------------------------------------
 * exportBook — canonical export call
 *   - hits POST /api/book-lab/books/:id/exports/:fmt
 *   - reads Content-Disposition filename (falls back to sanitized title)
 *   - triggers browser download via Blob + URL.createObjectURL
 *   - throws typed Error whose .status carries HTTP status
 * ------------------------------------------------------------------ */
export async function exportBook({ bookId, format, titleHint = "book" }) {
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/book-lab/books/${bookId}/exports/${format}`;
  const res = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${api.token()}` },
  });
  if (!res.ok) {
    let detail = `Export failed (${res.status})`;
    try { const j = await res.json(); if (j.detail) detail = j.detail; } catch (_e) { /* body not JSON */ }
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  const disp = res.headers.get("content-disposition") || "";
  const m = /filename="?([^";]+)"?/i.exec(disp);
  const fallback = `${(titleHint || "book").replace(/[^A-Za-z0-9\- _]+/g, "").replace(/\s+/g, "_")}.${format}`;
  const filename = m ? m[1] : fallback;
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  return {
    format,
    filename,
    size: blob.size,
    revision: res.headers.get("x-book-revision"),
    chapters: Number(res.headers.get("x-chapter-count") || 0),
  };
}

/* Public list of formats + labels. Kept single-source so no UI drift. */
export const EXPORT_FORMATS = [
  { key: "pdf",  label: "PDF",  icon: FileText, desc: "Print-ready manuscript" },
  { key: "docx", label: "Word (.docx)", icon: FileType, desc: "Editable in Microsoft Word / Google Docs" },
  { key: "epub", label: "EPUB", icon: BookOpen, desc: "Reflowable e-book (Kindle Previewer / iBooks)" },
  { key: "txt",  label: "Plain text (.txt)", icon: Code2, desc: "Clean UTF-8, no styling" },
  { key: "html", label: "HTML", icon: Code2, desc: "Self-contained, no scripts" },
];

/* ------------------------------------------------------------------
 * ExportBookModal — reusable modal (AI runner + Canvas Quick Actions).
 * Props:
 *   bookId, bookTitle, onClose, onSaveBeforeExport (optional async fn
 *     called before each export so unsaved chapter text is persisted).
 * ------------------------------------------------------------------ */
export function ExportBookModal({ bookId, bookTitle = "book", onClose, onSaveBeforeExport }) {
  const [busy, setBusy] = useState(null);   // active format key
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);

  const run = async (fmt) => {
    if (busy) return;    // prevent duplicate clicks
    setBusy(fmt); setErr(null); setResult(null);
    try {
      if (onSaveBeforeExport) {
        try { await onSaveBeforeExport(); }
        catch (e) {
          throw new Error(`Could not save latest edits before export: ${e.message || e}`);
        }
      }
      const r = await exportBook({ bookId, format: fmt, titleHint: bookTitle });
      setResult(r);
    } catch (e) {
      setErr(e.message || `Export failed`);
    } finally { setBusy(null); }
  };

  return (
    <div className="fixed inset-0 z-[100000] bg-black/60 backdrop-blur-sm grid place-items-center p-4"
         onClick={onClose} data-testid="export-book-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-md rounded-xl border border-[var(--pl-border)] bg-[var(--pl-bg)] shadow-2xl"
           style={{ color: "var(--pl-text)" }}>
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--pl-border)]">
          <Download className="h-4 w-4 text-[var(--pl-aqua)]" />
          <div className="text-[13px] font-bold">Export book</div>
          <div className="flex-1" />
          <button onClick={onClose} aria-label="Close" data-testid="export-book-close"
                  className="h-8 w-8 grid place-items-center rounded-md border border-[var(--pl-border)] hover:border-[var(--pl-aqua)]/40">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="p-3 space-y-1.5">
          {EXPORT_FORMATS.map((f) => {
            const I = f.icon;
            const active = busy === f.key;
            const disabled = busy && busy !== f.key;
            return (
              <button
                key={f.key}
                data-testid={`export-book-${f.key}`}
                onClick={() => run(f.key)}
                disabled={disabled}
                className={"w-full text-left flex items-center gap-3 rounded-lg border transition p-3 " +
                  (disabled ? "opacity-50 cursor-not-allowed border-[var(--pl-border)]"
                            : "border-[var(--pl-border)] hover:border-[var(--pl-aqua)]/50 hover:bg-white/[0.03]")}
              >
                <div className="h-8 w-8 rounded-md grid place-items-center bg-white/[0.04]">
                  {active ? <Loader2 className="h-4 w-4 animate-spin text-[var(--pl-aqua)]" />
                          : <I className="h-4 w-4 text-[var(--pl-aqua)]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold">{f.label}</div>
                  <div className="text-[11px] text-[var(--pl-text-muted)] mt-0.5">{f.desc}</div>
                </div>
                <div className="text-[10.5px] text-[var(--pl-text-muted)]">
                  {active ? "Preparing…" : `.${f.key}`}
                </div>
              </button>
            );
          })}
        </div>
        {err && (
          <div data-testid="export-book-error"
               className="mx-3 mb-3 p-2 rounded-md text-[11.5px] leading-snug"
               style={{ background: "rgba(220,38,38,0.12)", color: "#FCA5A5" }}>
            {err}
          </div>
        )}
        {result && (
          <div data-testid="export-book-success"
               className="mx-3 mb-3 p-2 rounded-md text-[11.5px] leading-snug"
               style={{ background: "rgba(34,197,94,0.12)", color: "#86EFAC" }}>
            Downloaded <strong>{result.filename}</strong>
            {" · "}{(result.size / 1024).toFixed(1)} KB
            {result.chapters ? ` · ${result.chapters} chapters` : ""}
          </div>
        )}
        <div className="border-t border-[var(--pl-border)] px-3 py-2 text-[10.5px] text-[var(--pl-text-muted)]">
          Every export renders from the same saved book data — canvas edits and AI-runner
          edits stay in sync via the canonical <code>books</code> + <code>book_chapters</code> model.
        </div>
      </div>
    </div>
  );
}


/* Small trigger button that opens the modal — for reuse everywhere. */
export function ExportBookButton({ bookId, bookTitle, testId = "export-book-button",
                                   onSaveBeforeExport, buttonClass, children }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid={testId} onClick={() => setOpen(true)}
              className={buttonClass}>
        {children || (<><Download className="h-3.5 w-3.5" /> Export</>)}
      </button>
      {open && (
        <ExportBookModal
          bookId={bookId} bookTitle={bookTitle}
          onClose={() => setOpen(false)}
          onSaveBeforeExport={onSaveBeforeExport}
        />
      )}
    </>
  );
}
